import { FC, ReactNode, memo } from 'react';
import { HelpCircle } from 'lucide-react';
import { Icon } from 'features/common/components/Icon';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import styles from './PunchupTile.module.css';

// ---------------------------------------------------------------------------
// Exported sub-components
// ---------------------------------------------------------------------------

/**
 * Visual treatment of a tile.
 *   'default'   — the standard tile: sentence-case label.
 *   'highlight' — the dashboard highlights strip: uppercase letter-spaced eyebrow label.
 * A string enum rather than a 6th boolean prop (root CLAUDE.md — "prop explosion").
 */
export type TileVariant = 'default' | 'highlight';

/**
 * Icon slot for the top-right header position.
 * Used by the Analytics KPI tiles via the topRight prop.
 * Leaf components: `topRight={<TileIcon iconName="dollarSignWhite" />}`
 *
 * Not used by highlight tiles — that strip is iconless by design (PUN-4943).
 */
export const TileIcon: FC<{ iconName: string }> = ({ iconName }) => (
	<Icon iconName={iconName} className={styles.tileIcon} />
);

/**
 * Bottom-anchored entry line — "go here" text in brand orange with a trailing arrow.
 * The arrow is decorative (the label already says where the tile leads), so it is hidden
 * from assistive tech. `.tileEntryLink`'s margin-top:auto is what pins it to the tile's
 * bottom edge; see the CSS module for why that lands every tile's line on one baseline.
 */
const TileEntryLink: FC<{ label: string }> = ({ label }) => (
	<div className={styles.tileEntryLink}>
		{label}
		<span aria-hidden>→</span>
	</div>
);

export const TileTagsRow: FC<{ children: ReactNode }> = ({ children }) => (
	<div className={styles.tileTagsRow}>{children}</div>
);

export const TileTag: FC<{ children: ReactNode }> = ({ children }) => (
	<span className={styles.tileTag}>{children}</span>
);

/**
 * Left-edge colour rail — the ticket type's colour code carried onto the tile. Renders nothing when
 * no colour is given. The colour is per-ticket DATA, so it is set inline; the shape/position is the
 * shared `.colorRail` utility. The tile's `position: relative` (`.tile`) anchors it.
 */
const TileColorRail: FC<{ color?: string }> = ({ color }) =>
	color ? <span className={styles.colorRail} style={{ backgroundColor: color }} aria-hidden /> : null;

/**
 * Grid for Dashboard highlight stat tiles.
 * className is forwarded so callers can extend it via styled(PunchupStatTileGrid).
 */
export const PunchupStatTileGrid: FC<{ children: ReactNode; className?: string }> = ({ children, className = '' }) => (
	<div className={`${styles.statTileGrid}${className ? ` ${className}` : ''}`}>{children}</div>
);

/**
 * Grid for entity cards (Ticket Types, Pools).
 * className is forwarded so TicketTypesSection can extend via styled(PunchupCardTileGrid).
 */
export const PunchupCardTileGrid: FC<{ children: ReactNode; className?: string }> = ({ children, className = '' }) => (
	<div className={`${styles.cardTileGrid}${className ? ` ${className}` : ''}`}>{children}</div>
);

/**
 * Single-column grid for text-heavy entity cards (Custom Fields).
 * Forces one tile per row so long question text is never clipped.
 */
export const PunchupSingleColumnTileGrid: FC<{ children: ReactNode }> = ({ children }) => (
	<div className={styles.singleColumnTileGrid}>{children}</div>
);

// ---------------------------------------------------------------------------
// Internal shared helper (not exported)
// ---------------------------------------------------------------------------

interface TileSharedProps {
	title?: string;
	topRight?: ReactNode;
	priceLabel?: string;
	primaryText: ReactNode; // the entity name or stat value
	primaryClassName: string; // styles.tileValue OR styles.tileEntityName
	descriptions?: string[];
	singleLineDescriptions?: boolean;
	singleLineTitle?: boolean;
	tagRow?: { label?: string; tags: string[] };
	tagSlot?: ReactNode;
	helpText?: string;
	helpButton?: ReactNode;
	variant?: TileVariant;
	entryLabel?: string;
}

/** Four title treatments from two independent axes — variant × truncation. */
function titleClassFor(variant: TileVariant, singleLine: boolean): string {
	if (variant === 'highlight') {
		return singleLine ? styles.tileTitleHighlightSingleLine : styles.tileTitleHighlight;
	}
	return singleLine ? styles.tileTitleSingleLine : styles.tileTitle;
}

function renderTileContent({
	title,
	topRight,
	priceLabel,
	primaryText,
	primaryClassName,
	descriptions,
	singleLineDescriptions,
	singleLineTitle,
	tagRow,
	tagSlot,
	helpText,
	helpButton,
	variant = 'default',
	entryLabel,
}: TileSharedProps): ReactNode {
	const showHeader = !!(title || topRight);
	const hasPrice = !!priceLabel;
	const descClass = singleLineDescriptions ? styles.tileDescriptionSingleLine : styles.tileDescription;
	const titleClass = titleClassFor(variant, !!singleLineTitle);

	const mainContent = (
		<>
			{showHeader && (
				<div className={styles.tileHeader}>
					{title && (
						<div className={titleClass} title={singleLineTitle ? title : undefined}>
							{title}
						</div>
					)}
					{topRight && <div className={styles.tileHeaderRight}>{topRight}</div>}
				</div>
			)}

			{primaryText !== undefined && <div className={primaryClassName}>{primaryText}</div>}

			{descriptions?.map((desc, i) => (
				<div key={i} className={descClass} title={desc || undefined}>
					{desc}
				</div>
			))}

			{tagSlot ? (
				// Pre-rendered chips (e.g. colour-tinted ticket-type tags for pool members). The caller
				// composes the chips — the tile only lays out the row — so a primitive doesn't import
				// another primitive (PunchupTicketTypeTag).
				<div className={styles.tileTagSection}>
					<div className={styles.tileTagsRow}>{tagSlot}</div>
				</div>
			) : tagRow && tagRow.tags.length > 0 ? (
				<div className={styles.tileTagSection}>
					{/* Label is optional — a label-less tag row (e.g. the pool tile's member chips) omits it. */}
					{tagRow.label && <div className={styles.tileTagRowLabel}>{tagRow.label}</div>}
					<div className={styles.tileTagsRow}>
						{tagRow.tags.map((tag) => (
							<span key={tag} className={styles.tileTag}>
								{tag}
							</span>
						))}
					</div>
				</div>
			) : null}

			{entryLabel && <TileEntryLink label={entryLabel} />}
		</>
	);

	const helpSlot =
		helpButton ??
		(helpText ? (
			<span className={styles.helpIconBtn} aria-label="Help">
				<HelpCircle size={12} />
				<span role="tooltip" className={styles.helpTooltip}>
					{helpText}
				</span>
			</span>
		) : null);

	if (hasPrice) {
		return (
			<>
				<div className={styles.tileMainContent}>{mainContent}</div>
				<div className={styles.tilePriceColumn}>
					<span className={styles.tilePriceAmount}>{priceLabel}</span>
				</div>
				{helpSlot}
			</>
		);
	}
	if (helpSlot) {
		return (
			<>
				{mainContent}
				{helpSlot}
			</>
		);
	}
	return mainContent;
}

// ---------------------------------------------------------------------------
// Shared render shell (not exported)
// ---------------------------------------------------------------------------

interface TileShellProps {
	tileClass: string;
	isClickable: boolean;
	onClick?: () => void;
	disabled?: boolean;
	dataCy?: string;
	railColor?: string;
	content: ReactNode;
}

/**
 * The tile chrome — colour rail + content wrapped in a button (when clickable) or a div.
 * Each tile variant computes its own clickability gate (PunchupTile also gates on isLoading;
 * PunchupEntityTile gates on onClick alone) and passes it in, so the gate stays per-variant
 * while the shell stays in one place.
 */
function renderTileShell({
	tileClass,
	isClickable,
	onClick,
	disabled,
	dataCy,
	railColor,
	content,
}: TileShellProps): JSX.Element {
	if (isClickable) {
		return (
			<button className={tileClass} onClick={onClick} disabled={disabled} type="button" data-cy={dataCy}>
				<TileColorRail color={railColor} />
				{content}
			</button>
		);
	}

	return (
		<div className={tileClass} data-cy={dataCy}>
			<TileColorRail color={railColor} />
			{content}
		</div>
	);
}

// ---------------------------------------------------------------------------
// PunchupTileProps
// ---------------------------------------------------------------------------

export interface PunchupTileProps {
	/**
	 * Small label shown top-left (≈ CardTitle in PunchupLive).
	 * Optional — when absent (e.g. pool with no description) the header row is omitted.
	 */
	title?: string;
	/**
	 * Generic slot for the top-right corner of the header.
	 * Leaf components decide what goes here:
	 *   - Stat tiles: <TileIcon iconName={icon} />  (imported from PunchupTile)
	 *   - Ticket tiles: nothing (price is in the stub column instead)
	 *   - Pool tiles: nothing
	 */
	topRight?: ReactNode;
	/**
	 * When provided, switches the tile to a ticket-stub layout:
	 * main content sits in a left column; price appears in a right column.
	 * The layout change (flex → 2-column grid) lives here so the price column
	 * is a sibling of the main content at the button level.
	 * For PWYW tickets pass `undefined` and add "Pay what you want" to descriptions instead.
	 */
	priceLabel?: string;
	/**
	 * Bold main content (26px / 700 weight).
	 * Use for stat numbers ("$1,234", "142 sold"). Clamped to 2 lines.
	 * For entity names (ticket types, pools, custom fields), use PunchupEntityTile.name instead.
	 */
	value?: ReactNode;
	/**
	 * When true, renders the value at 16px / 600 weight instead of the default 26px / 700.
	 * Use in narrow grids where large stat numbers would overflow the tile width
	 * (e.g. 5–9 digit revenue figures in the analytics inventory grid).
	 */
	compact?: boolean;
	/**
	 * One or more small muted description lines below the value.
	 * Each line is independently 2-line clamped.
	 */
	descriptions?: string[];
	/**
	 * Optional bottom row: a small label followed by a row of orange pill chips.
	 * Used for entity collections, e.g. `{ label: 'Ticket types', tags: ['VIP', 'GA'] }`.
	 * Only rendered when tags.length > 0.
	 * Each tag is truncated at 160px to prevent layout overflow.
	 */
	tagRow?: { label?: string; tags: string[] };
	/**
	 * Pre-rendered chip row for the bottom tag area — use INSTEAD of `tagRow` when the chips are not
	 * plain orange pills (e.g. colour-tinted `PunchupTicketTypeTag` pool-member chips). The caller
	 * composes the chips; the tile only lays out the row. Takes precedence over `tagRow`. Wrap the node
	 * in `useMemo` at the callsite — `PunchupTile` is memoised and a fresh node each render misses memo.
	 */
	tagSlot?: ReactNode;
	/**
	 * Ticket-type colour code shown as a thin left-edge rail (see `.colorRail`). Per-ticket data — pass
	 * the resolved hex from `useTicketTypeColors`. Omit for tiles with no ticket-type identity.
	 */
	railColor?: string;
	/**
	 * When provided the entire tile becomes a clickable button.
	 * Hover animates the border to punchupOrange and adds shadow-md equivalent.
	 * When absent the tile is informational: background shifts to surface colour
	 * (subtle visual cue that no action is available — no opacity reduction,
	 * so text contrast is maintained).
	 */
	onClick?: () => void;
	/** Disables click and reduces tile opacity. Background reverts to white. */
	disabled?: boolean;
	dataCy?: string;
	/**
	 * When provided, a HelpCircle icon appears in the tile header.
	 * On hover it shows this text as a tooltip explaining the metric, its formula,
	 * and what a high or low value means.
	 * @deprecated Use helpButton with PunchupHelpButton for rich 3-segment tooltips.
	 */
	helpText?: string;
	/**
	 * Replaces the CSS-only helpText tooltip with a portal-based PunchupHelpButton.
	 * Pass <PunchupHelpButton mainText="..." codeText="..." secondaryText="..." />.
	 * Takes precedence over helpText when both are provided.
	 */
	helpButton?: ReactNode;
	/**
	 * When true, description rows are truncated to a single line with ellipsis
	 * instead of the default 2-line clamp. Use when tiles must be uniform in height.
	 */
	singleLineDescriptions?: boolean;
	/**
	 * When true, the title is truncated to a single line with ellipsis and the
	 * full text is exposed via the native `title` attribute (browser tooltip on hover).
	 * Use in narrow grids where long names must not displace the value row.
	 */
	singleLineTitle?: boolean;
	/**
	 * When true, renders a loading spinner in place of `value`.
	 * The tile layout (title, topRight, descriptions) is preserved.
	 * onClick is suppressed — the tile is non-interactive while loading.
	 */
	isLoading?: boolean;
	/**
	 * Visual treatment. Defaults to 'default'.
	 * 'highlight' switches the title to an uppercase, letter-spaced eyebrow label — the
	 * dashboard highlights-strip look.
	 */
	variant?: TileVariant;
	/**
	 * Bottom-anchored orange entry line with a trailing arrow — "View transactions →".
	 * Names where the tile leads, so pass it together with `onClick`.
	 * It is pinned to the tile's bottom edge, which puts every tile's line on one baseline
	 * across a grid row regardless of how much content sits above it.
	 */
	entryLabel?: string;
}

// ---------------------------------------------------------------------------
// PunchupTile
// ---------------------------------------------------------------------------

/**
 * PunchupTile — the canonical interactive stat tile for Punchup pages.
 *
 * Use for stat numbers ("$1,234", "142 sold") displayed at 26px / 700 weight.
 * For entity names (ticket types, pools, custom fields), use PunchupEntityTile instead.
 *
 * Mirrors the PunchupLive Waitlist stat-card pattern.
 * The WHOLE tile is the button — no separate "Edit" / "View" child button.
 *
 * // 1. Analytics KPI (topRight = muted icon via TileIcon from this module)
 * <PunchupTile title="Your Payout"
 *              topRight={<TileIcon iconName="dollarSignWhite" />}
 *              value="$1,234"
 *              onClick={goToOrders} />
 *
 * // 2. Dashboard highlight (iconless eyebrow label + bottom-anchored orange entry line)
 * <PunchupTile variant="highlight"
 *              title="Your Payout"
 *              value="$1,234"
 *              entryLabel="View transactions"
 *              onClick={goToOrders} />
 */
export const PunchupTile = memo<PunchupTileProps>(
	({
		title,
		topRight,
		priceLabel,
		value,
		descriptions,
		tagRow,
		tagSlot,
		railColor,
		onClick,
		disabled,
		dataCy,
		helpText,
		helpButton,
		singleLineDescriptions,
		singleLineTitle,
		compact,
		isLoading,
		variant,
		entryLabel,
	}) => {
		// .clickable is applied for ALL onClick tiles — active and disabled alike — so
		// the white background and interactive shape are visible even in the disabled state.
		// Suppressed while loading: the tile is informational until data arrives.
		const hasPrice = !!priceLabel;
		const isClickable = !!onClick && !isLoading;

		const tileClass = [
			styles.tile,
			isClickable && styles.clickable,
			hasPrice && styles.hasPrice,
			railColor && styles.hasRail,
		]
			.filter(Boolean)
			.join(' ');

		const content = renderTileContent({
			title,
			topRight,
			priceLabel,
			primaryText: isLoading ? <PunchupSpinner size={24} /> : value,
			primaryClassName: compact ? styles.tileEntityName : styles.tileValue,
			descriptions,
			singleLineDescriptions,
			singleLineTitle,
			tagRow,
			tagSlot,
			helpText,
			helpButton,
			variant,
			entryLabel,
		});

		return renderTileShell({ tileClass, isClickable, onClick, disabled, dataCy, railColor, content });
	}
);

PunchupTile.displayName = 'PunchupTile';

// ---------------------------------------------------------------------------
// PunchupEntityTileProps
// ---------------------------------------------------------------------------

export interface PunchupEntityTileProps {
	/**
	 * Primary entity name — displayed at 16px / 600 weight.
	 * Use this instead of PunchupTile when the primary text is a name, title, or
	 * question rather than a stat number. Clamped to 2 lines.
	 */
	name: string;
	/** Same semantics as PunchupTile.title — small label top-left. */
	title?: string;
	/** Same semantics as PunchupTile.topRight. */
	topRight?: ReactNode;
	/** Same semantics as PunchupTile.priceLabel — activates ticket-stub 2-column layout. */
	priceLabel?: string;
	/** Same semantics as PunchupTile.descriptions. */
	descriptions?: string[];
	/**
	 * Same semantics as PunchupTile.tagRow.
	 * Note: if this is an object literal at the callsite (`{ label, tags }`), wrap it in
	 * useMemo — memo skips equality checks on new object references created each render.
	 */
	tagRow?: { label?: string; tags: string[] };
	/** Same semantics as PunchupTile.tagSlot — pre-rendered chips (e.g. colour-tinted member tags). */
	tagSlot?: ReactNode;
	/** Same semantics as PunchupTile.railColor — ticket-type colour code shown as a left-edge rail. */
	railColor?: string;
	/** Same semantics as PunchupTile.onClick. */
	onClick?: () => void;
	/** Same semantics as PunchupTile.disabled. */
	disabled?: boolean;
	dataCy?: string;
	/** Same semantics as PunchupTile.helpText. @deprecated Use helpButton. */
	helpText?: string;
	/** Same semantics as PunchupTile.helpButton. */
	helpButton?: ReactNode;
	/** Same semantics as PunchupTile.singleLineDescriptions. */
	singleLineDescriptions?: boolean;
	/** Same semantics as PunchupTile.singleLineTitle. */
	singleLineTitle?: boolean;
}

// ---------------------------------------------------------------------------
// PunchupEntityTile
// ---------------------------------------------------------------------------

/**
 * PunchupEntityTile — tile for named entities (ticket types, pools, custom fields).
 *
 * Identical structure to PunchupTile but renders the primary text at 16px / 600
 * instead of 26px / 700. Use when the primary content is a name or phrase, not
 * a stat number. The `name: string` prop enforces this at the TypeScript level.
 *
 * // Ticket type (with ticket-stub price column)
 * <PunchupEntityTile
 *   title="On sale · ends Dec 12, 2024"
 *   priceLabel="$25.00"
 *   name="GA Ticket"
 *   descriptions={["42 / 100 sold"]}
 *   onClick={goToEdit}
 * />
 *
 * // Pool
 * <PunchupEntityTile
 *   title={pool.description || undefined}
 *   name={pool.name}
 *   descriptions={["10 / 50 sold"]}
 *   tagRow={{ label: 'Ticket types', tags: [...] }}
 *   onClick={goToEdit}
 * />
 *
 * // Custom field
 * <PunchupEntityTile
 *   name={field.question}
 *   descriptions={[field.type, field.required ? 'Required' : 'Optional']}
 *   onClick={goToEdit}
 * />
 *
 * forwardRef: not implemented — no drag-to-reorder for entity tiles yet.
 * Add forwardRef if a drag library needs a DOM ref on the tile button.
 */
export const PunchupEntityTile = memo<PunchupEntityTileProps>(
	({
		name,
		title,
		topRight,
		priceLabel,
		descriptions,
		tagRow,
		tagSlot,
		railColor,
		onClick,
		disabled,
		dataCy,
		helpText,
		helpButton,
		singleLineDescriptions,
		singleLineTitle,
	}) => {
		const hasPrice = !!priceLabel;

		const tileClass = [
			styles.tile,
			onClick && styles.clickable,
			hasPrice && styles.hasPrice,
			railColor && styles.hasRail,
		]
			.filter(Boolean)
			.join(' ');

		const content = renderTileContent({
			title,
			topRight,
			priceLabel,
			primaryText: name,
			primaryClassName: styles.tileEntityName,
			descriptions,
			singleLineDescriptions,
			singleLineTitle,
			tagRow,
			tagSlot,
			helpText,
			helpButton,
		});

		return renderTileShell({ tileClass, isClickable: !!onClick, onClick, disabled, dataCy, railColor, content });
	}
);

PunchupEntityTile.displayName = 'PunchupEntityTile';
